"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"
import { PhotoCard } from "./photo-card"
import { Camera, Loader2 } from "lucide-react"

export type CommunitySort = 'newest' | 'top-rated' | 'most-viewed' | 'trending' | 'controversial'

export interface CommunityGalleryFilters {
  sortBy: CommunitySort
  grassType?: string
  region?: string
}

export interface LawnPhoto {
  id: string
  slug: string
  image_url: string
  thumbnail_url?: string
  title: string
  score: number
  upvotes: number
  downvotes: number
  grass_type?: string
  zip_code?: string
  created_at: string
  view_count?: number
  share_count?: number
  social_handle?: string
}

interface PhotoGridProps extends CommunityGalleryFilters {
  limit?: number
  initialFilters?: CommunityGalleryFilters
  initialPhotos?: LawnPhoto[]
  initialPage?: number
  initialHasMore?: boolean
  initialError?: string | null
}

function filterKey(filters: CommunityGalleryFilters): string {
  return [filters.sortBy, filters.grassType || '', filters.region || ''].join('|')
}

export function buildCommunityPageHref(
  page: number,
  { sortBy, grassType, region }: CommunityGalleryFilters,
): string {
  const params = new URLSearchParams()
  if (page > 1) params.set('page', String(page))
  if (sortBy !== 'newest') params.set('sortBy', sortBy)
  if (grassType) params.set('grassType', grassType)
  if (region) params.set('region', region)

  const query = params.toString()
  return query ? `/community?${query}` : '/community'
}

export function PhotoGrid({
  limit = 20,
  sortBy = 'newest',
  grassType,
  region,
  initialFilters,
  initialPhotos = [],
  initialPage = 1,
  initialHasMore = false,
  initialError = null,
}: PhotoGridProps) {
  const filters = { sortBy, grassType, region }
  const activeFilterKey = filterKey(filters)
  const initialFilterKey = filterKey(initialFilters || filters)
  const [photos, setPhotos] = useState<LawnPhoto[]>(initialPhotos)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(initialError)
  const [hasMore, setHasMore] = useState(initialHasMore)
  const [currentPage, setCurrentPage] = useState(initialPage)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLAnchorElement>(null)
  const requestGenerationRef = useRef(0)
  const abortControllerRef = useRef<AbortController | null>(null)
  const previousFilterKeyRef = useRef(initialFilterKey)

  useEffect(() => () => abortControllerRef.current?.abort(), [])

  const fetchPhotos = useCallback(async ({ page, replace }: { page: number; replace: boolean }) => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    const generation = requestGenerationRef.current + 1
    requestGenerationRef.current = generation

    try {
      if (replace) setLoading(true)
      else setLoadingMore(true)
      setError(null)

      // Request one extra row so a full page does not create a false "next"
      // link when it is actually the final cohort.
      const requestedLimit = Math.min(limit + 1, 50)
      const params = new URLSearchParams({
        limit: requestedLimit.toString(),
        offset: ((page - 1) * limit).toString(),
        sortBy,
        ...(grassType && { grassType }),
        ...(region && { region }),
      })

      const response = await fetch(`/api/community/photos?${params}`, {
        signal: controller.signal,
      })
      if (!response.ok) throw new Error('Failed to fetch photos')

      const data = await response.json()
      const fetchedPhotos: LawnPhoto[] = data.photos || []
      if (controller.signal.aborted || generation !== requestGenerationRef.current) return

      const pagePhotos = fetchedPhotos.slice(0, limit)
      setPhotos((previous) => {
        if (replace) return pagePhotos
        const knownIds = new Set(previous.map((photo) => photo.id))
        return [...previous, ...pagePhotos.filter((photo) => !knownIds.has(photo.id))]
      })
      setCurrentPage(page)
      setHasMore(fetchedPhotos.length > limit)
    } catch (fetchError) {
      if (fetchError instanceof Error && fetchError.name === 'AbortError') return
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to load photos')
      console.error('Error fetching photos:', fetchError)
    } finally {
      if (generation === requestGenerationRef.current) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [grassType, limit, region, sortBy])

  // The server payload is the first paint. Only fetch after a visitor changes a
  // filter, avoiding the old hydration-time duplicate request.
  useEffect(() => {
    if (previousFilterKeyRef.current === activeFilterKey) return
    previousFilterKeyRef.current = activeFilterKey
    setPhotos([])
    setCurrentPage(1)
    setHasMore(false)
    void fetchPhotos({ page: 1, replace: true })
  }, [activeFilterKey, fetchPhotos])

  // A query-param navigation can replace the server cohort without remounting
  // this client boundary. This effect intentionally follows the filter-fetch
  // effect: on an RSC navigation the old active filter is skipped first, then
  // the new server cohort becomes the current one without a duplicate request.
  useEffect(() => {
    abortControllerRef.current?.abort()
    setPhotos(initialPhotos)
    setCurrentPage(initialPage)
    setHasMore(initialHasMore)
    setError(initialError)
    setLoading(false)
    setLoadingMore(false)
    previousFilterKeyRef.current = initialFilterKey
  }, [initialError, initialFilterKey, initialHasMore, initialPage, initialPhotos])

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return
    void fetchPhotos({ page: currentPage + 1, replace: false })
  }, [currentPage, fetchPhotos, hasMore, loading, loadingMore])

  // Preserve the convenient infinite-scroll behavior for people while the
  // visible anchor remains a crawlable fallback for bots and no-JS visitors.
  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !hasMore || loading || loadingMore || error) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore()
      },
      { threshold: 0.1, rootMargin: '100px' },
    )
    observerRef.current = observer
    observer.observe(target)

    return () => observer.disconnect()
  }, [error, hasMore, loadMore, loading, loadingMore])

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="ds-glass overflow-hidden rounded-3xl animate-pulse">
            <div className="aspect-4/3 bg-green-100"></div>
            <div className="p-2 md:p-4 space-y-2">
              <div className="h-3 md:h-4 bg-gray-200 rounded"></div>
              <div className="h-2 md:h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error && photos.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load photos</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          type="button"
          onClick={() => void fetchPhotos({ page: 1, replace: true })}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (photos.length === 0) {
    const hasActiveFilters = Boolean(grassType || region)
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {hasActiveFilters ? 'No lawns match these filters' : 'The gallery is ready for its first lawn'}
        </h3>
        <p className="text-gray-600">
          {hasActiveFilters
            ? 'Try another grass type or region, or add a lawn from your area.'
            : 'Share a lawn photo to help start the community gallery.'}
        </p>
        <Link
          href="/community/submit"
          className="mt-5 inline-flex items-center justify-center min-h-[44px] rounded-lg bg-green-600 px-5 text-sm font-semibold text-white hover:bg-green-700 transition-colors"
        >
          Share your lawn
        </Link>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-6">
        {photos.map((photo, index) => (
          <PhotoCard key={photo.id} photo={photo} eager={index < 3} />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 py-8">
        {currentPage > 1 && (
          <Link
            href={buildCommunityPageHref(currentPage - 1, filters)}
            className="inline-flex min-h-11 items-center rounded-full border border-green-200 bg-white px-5 text-sm font-semibold text-green-800 shadow-sm hover:bg-green-50"
          >
            Previous lawns
          </Link>
        )}

        {hasMore && (
          <Link
            ref={loadMoreRef}
            href={buildCommunityPageHref(currentPage + 1, filters)}
            onClick={(event) => {
              event.preventDefault()
              loadMore()
            }}
            aria-disabled={loadingMore}
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-green-200 bg-white px-5 text-sm font-semibold text-green-800 shadow-sm hover:bg-green-50 aria-disabled:cursor-wait aria-disabled:opacity-70"
          >
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {loadingMore ? 'Loading more lawns...' : 'Load more lawns'}
          </Link>
        )}
      </div>

      {error && photos.length > 0 && (
        <div className="-mt-4 text-center text-sm text-red-700" role="alert">
          More lawns could not load. Try again in a moment.
        </div>
      )}

      {!hasMore && photos.length > 0 && (
        <div className="mt-8 md:mt-12">
          <Link
            href="/community/submit"
            className="group mx-auto block max-w-md rounded-3xl border border-dashed border-green-300 bg-white p-6 text-center transition-colors hover:border-green-500 hover:bg-green-50/50"
          >
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
              <Camera className="w-6 h-6 text-green-600" aria-hidden="true" />
            </div>
            <p className="font-semibold text-green-950 mb-1">Got a nice lawn?</p>
            <p className="text-sm text-green-950/55">Share your photo with the community</p>
          </Link>
        </div>
      )}
    </>
  )
}
